import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, PenNib } from "@phosphor-icons/react";

const RELATIONSHIPS = ["Mother", "Father", "Parent", "Stepmother", "Stepfather", "Grandmother", "Grandfather", "Legal guardian",
                       "Foster parent", "Aunt", "Uncle", "Other authorized adult"];
export const STATEMENT = "I certify that I am the parent, legal guardian, or authorized person identified above. By typing my full legal name and selecting the checkbox below, I understand that I am electronically signing this document and that my electronic signature has the same effect as a handwritten signature.";

/** Family signs a form for one child: answers + typed legal name + intentional agreement checkbox. */
export default function SignForm() {
  const { formId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [child, setChild] = useState(null);
  const [answers, setAnswers] = useState({});
  const [sig, setSig] = useState({ signer_name: "", relationship: "", agree: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const studentId = params.get("student");

  useEffect(() => {
    Promise.all([api.get(`/public/forms/${formId}`), api.get(`/students/${studentId}`)])
      .then(([f, s]) => { setForm(f.data); setChild(s.data); })
      .catch((e) => setError(apiError(e.response?.data?.detail)));
  }, [formId, studentId]);

  const set = (k, v) => setAnswers({ ...answers, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/forms/${formId}/sign`, { student_id: studentId, answers, ...sig });
      toast.success("Signed — a copy was saved to your child's records");
      nav(`/portal/signed/${data.signature_id}`, { replace: true });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  if (error) return <div className="text-[#EF4444] font-semibold" data-testid="sign-error">{error}</div>;
  if (!form || !child) return <p className="text-muted-foreground" data-testid="sign-loading">Loading form…</p>;

  const field = (f) => {
    const v = answers[f.label] ?? "";
    const tid = `sign-field-${f.label.replace(/\s+/g, "-").toLowerCase()}`;
    if (f.type === "signature") return null;
    if (f.type === "long_text") return <Textarea value={v} onChange={(e) => set(f.label, e.target.value)} className="mt-2 rounded-xl min-h-24 text-base" data-testid={tid} />;
    if (f.type === "yes_no" || f.type === "select" || f.type === "multiselect")
      return (
        <Select value={v} onValueChange={(x) => set(f.label, x)}>
          <SelectTrigger className="mt-2 h-12 rounded-xl text-base" data-testid={tid}><SelectValue placeholder="Choose" /></SelectTrigger>
          <SelectContent>{(f.type === "yes_no" ? ["Yes", "No"] : f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      );
    if (f.type === "checkbox")
      return <label className="mt-2 flex items-center gap-3 text-base font-semibold"><input type="checkbox" className="h-5 w-5" checked={v === "Yes"} onChange={(e) => set(f.label, e.target.checked ? "Yes" : "")} data-testid={tid} /> I agree</label>;
    return <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={v} onChange={(e) => set(f.label, e.target.value)} className="mt-2 h-12 rounded-xl text-base" data-testid={tid} />;
  };

  return (
    <div className="max-w-2xl" data-testid="sign-form-page">
      <Link to="/portal/forms" className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-[#3B82F6]" data-testid="sign-back"><ArrowLeft size={16} /> Back to forms</Link>
      <p className="mt-6 text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">{form.kind} · version {form.version || 1}</p>
      <h1 className="mt-2 text-4xl font-bold tracking-tight" data-testid="sign-title">{form.title}</h1>
      <p className="mt-2 text-base text-muted-foreground">For <strong className="text-foreground">{child.first_name} {child.last_name}</strong> · {child.room}</p>
      {form.description && <p className="mt-3 text-base text-muted-foreground">{form.description}</p>}

      <form onSubmit={submit} className="mt-8 space-y-6">
        {(form.fields || []).filter((f) => f.type !== "signature").length > 0 && (
          <div className="bg-white rounded-3xl border border-border p-6 space-y-5" data-testid="sign-questions">
            {(form.fields || []).map((f) => f.type === "signature" ? null : (
              <div key={f.label}><Label className="text-base font-bold">{f.label}{f.required ? " *" : ""}</Label>{field(f)}</div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-3xl border-2 border-[#3B82F6] p-6 space-y-5" data-testid="sign-block">
          <div className="flex items-center gap-3"><PenNib size={26} weight="duotone" className="text-[#3B82F6]" /><h2 className="font-display text-xl font-semibold">Electronic signature</h2></div>
          <p className="text-base leading-relaxed" data-testid="sign-statement">{STATEMENT}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-base font-bold">Your full legal name *</Label>
              <Input required value={sig.signer_name} onChange={(e) => setSig({ ...sig, signer_name: e.target.value })} className="mt-2 h-14 rounded-xl font-display text-xl" placeholder="Type your full name" data-testid="sign-name" /></div>
            <div><Label className="text-base font-bold">Relationship to {child.first_name} *</Label>
              <Select value={sig.relationship} onValueChange={(v) => setSig({ ...sig, relationship: v })}>
                <SelectTrigger className="mt-2 h-14 rounded-xl text-base" data-testid="sign-relationship"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>{RELATIONSHIPS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <label className="flex items-start gap-3 text-base font-semibold rounded-2xl bg-blue-50 p-4 cursor-pointer" data-testid="sign-agree-label">
            <input type="checkbox" className="mt-1 h-6 w-6" checked={sig.agree} onChange={(e) => setSig({ ...sig, agree: e.target.checked })} data-testid="sign-agree" />
            I agree and electronically sign this document
          </label>
          <p className="text-xs text-muted-foreground">Signed as {user.email}. The date, time and device information are recorded with your signature.</p>
          <Button type="submit" disabled={busy || !sig.agree || sig.signer_name.trim().length < 2 || !sig.relationship}
                  className="w-full h-14 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold text-base" data-testid="sign-submit">
            {busy ? "Signing…" : "Sign document"}</Button>
        </div>
      </form>
    </div>
  );
}
